import React, { useMemo, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import "./SetupReferencePages.css";
import { useSiteContext } from "../SiteContext";
import { PayPeriod } from "../interfaces/PayPeriodInterface";
import {
    Calendar,
    ChevronRight,
    ChevronUp,
    ChevronDown,
} from "lucide-react";

// ─── Types ───────────────────────────────────────────────────────────────────
type PPSortKey = "week" | "start" | "check" | "alt";

// ─── Constants ───────────────────────────────────────────────────────────────
const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Today's date as a YYYY-MM-DD string — computed once at module load. */
const TODAY_STR = (() => {
    const d = new Date();
    const y = d.getFullYear();
    const m = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    return `${y}-${m}-${day}`;
})();

// ─── Helpers ─────────────────────────────────────────────────────────────────
function cmp(a: string | number, b: string | number): number {
    if (typeof a === "number" && typeof b === "number") return a - b;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
}

/**
 * Extract YYYY-MM-DD safely from a Date object or ISO string.
 * Uses local date parts for Date objects to avoid timezone crossing.
 */
function toDatePart(d: Date | string): string {
    if (typeof d === "string") return d.slice(0, 10);
    const y = d.getFullYear();
    const m = (d.getMonth() + 1).toString().padStart(2, "0");
    const day = d.getDate().toString().padStart(2, "0");
    return `${y}-${m}-${day}`;
}

/**
 * Format a pay-period date as "MMM D, YYYY" without timezone shift.
 * Parses date-only from ISO strings to avoid UTC-vs-local issues.
 */
function fmtPayDate(d: Date | string): string {
    if (typeof d === "string") {
        const p = d.slice(0, 10).split("-");
        if (p.length === 3) {
            const mo = parseInt(p[1], 10) - 1;
            const day = parseInt(p[2], 10);
            const yr = parseInt(p[0], 10);
            if (mo >= 0 && mo <= 11) return `${MONTHS_SHORT[mo]} ${day}, ${yr}`;
        }
        return d; // unexpected format — return raw
    }
    return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function isCurrentWeek(p: PayPeriod): boolean {
    const start = toDatePart(p.StartDate);
    const end = toDatePart(p.EndDate);
    return TODAY_STR >= start && TODAY_STR <= end;
}

// ─── Component ───────────────────────────────────────────────────────────────
const PayPeriodsPage: React.FC = () => {
    // Read pay periods from context only (no loading — already loaded at login).
    // Do NOT read or mutate the global `year` / `setYear` from SiteContext.
    const { payPeriods, translator } = useSiteContext();

    // Local state — isolated from the global year context
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [sortKey, setSortKey] = useState<PPSortKey>("week");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

    // Derive distinct years from loaded data (ascending)
    const years = useMemo<number[]>(() => {
        const ys = [...new Set(payPeriods.map(p => p.AccountingYear))].sort((a, b) => a - b);
        return ys;
    }, [payPeriods]);

    // Effective year: selected by user, else current calendar year if present, else latest in data
    const currentCalYear = new Date().getFullYear();
    const effectiveYear = useMemo<number | null>(() => {
        if (selectedYear !== null) return selectedYear;
        if (years.length === 0) return null;
        return years.includes(currentCalYear) ? currentCalYear : years[years.length - 1];
    }, [selectedYear, years, currentCalYear]);

    // ─── Filter + sort ────────────────────────────────────────────────────────
    const processed = useMemo<PayPeriod[]>(() => {
        if (effectiveYear === null) return [];
        const rows = payPeriods.filter(p => p.AccountingYear === effectiveYear);
        const keyFn = (p: PayPeriod): string | number => {
            switch (sortKey) {
                case "week":  return p.WeekNumber;
                case "start": return toDatePart(p.StartDate);
                case "check": return p.CheckDate ? p.CheckDate.slice(0, 10) : "";
                case "alt":   return p.AltPayPeriodID || "";
                default:      return p.WeekNumber;
            }
        };
        return [...rows].sort((a, b) => cmp(keyFn(a), keyFn(b)) * (sortDir === "asc" ? 1 : -1));
    }, [payPeriods, effectiveYear, sortKey, sortDir]);

    const hasCurrentWeek = processed.some(isCurrentWeek);

    // ─── Sorting ──────────────────────────────────────────────────────────────
    const handleSort = (key: PPSortKey) => {
        if (sortKey === key) setSortDir(d => (d === "asc" ? "desc" : "asc"));
        else { setSortKey(key); setSortDir("asc"); }
    };

    // ─── Sortable header cell ─────────────────────────────────────────────────
    const Th: React.FC<{ label: string; sk?: PPSortKey }> = ({ label, sk }) => {
        const sortable = !!sk;
        const sorted = sortable && sortKey === sk;
        const desc = sorted && sortDir === "desc";
        return (
            <th
                className={`${sortable ? "sortable" : ""} ${sorted ? "sorted" : ""}`.trim() || undefined}
                onClick={sortable ? () => handleSort(sk!) : undefined}
            >
                <span className="th-in">
                    {label}
                    {sortable && (desc
                        ? <ChevronDown className="caret" size={13} />
                        : <ChevronUp className="caret" size={13} />
                    )}
                </span>
            </th>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <PageWrapper title={translator.translate("PayPeriods")} backgroundColor="#f4f5f7">
            <div className="setup-ref">

                {/* breadcrumb */}
                <div className="sr-crumbs">
                    <span>Setup</span>
                    <ChevronRight />
                    <span className="here">Pay Periods</span>
                </div>

                {/* header */}
                <div className="sr-app-head">
                    <div className="sr-logo"><Calendar size={23} /></div>
                    <div>
                        <h1 className="sr-app-title">Pay Periods</h1>
                        <div className="sr-app-sub">
                            The weekly payroll calendar. Each week (Sunday–Saturday) maps to a period ID used across timesheets.
                        </div>
                    </div>
                </div>

                {/* year segmented control (local, no global mutation) */}
                <div className="sr-toolbar">
                    {years.length > 0 && (
                        <div className="sr-field">
                            <span className="flab">Year</span>
                            <div className="sr-segmented" role="group">
                                {years.map(y => (
                                    <button
                                        key={y}
                                        type="button"
                                        className={effectiveYear === y ? "on" : ""}
                                        onClick={() => setSelectedYear(y)}
                                    >
                                        {y}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* result line */}
                <div className="sr-result-line">
                    <span>
                        <b>{processed.length}</b> weekly periods in <b>{effectiveYear ?? "—"}</b>
                    </span>
                    {hasCurrentWeek && (
                        <span className="rl-chip">
                            <Calendar size={12} /> Current week highlighted
                        </span>
                    )}
                </div>

                {/* table — no pager for pay periods */}
                <div className="sr-card">
                    <div className="sr-tbl-wrap">
                        <table className="sr-dt">
                            <thead>
                                <tr>
                                    <Th label="Week" sk="week" />
                                    <Th label="Start Date" sk="start" />
                                    <th>End Date</th>
                                    <Th label="Check Date" sk="check" />
                                    <th>Year</th>
                                    <Th label="ID" sk="alt" />
                                </tr>
                            </thead>
                            <tbody>
                                {processed.map(p => {
                                    const isCurrent = isCurrentWeek(p);
                                    return (
                                        <tr
                                            key={`${p.AccountingYear}-W${p.WeekNumber}`}
                                            className={isCurrent ? "now-row" : ""}
                                        >
                                            {/* Week */}
                                            <td>
                                                <span className="week-pill">
                                                    W{String(p.WeekNumber).padStart(2, "0")}
                                                </span>
                                                {isCurrent && (
                                                    <span className="now-badge">
                                                        <span className="dot" />Current
                                                    </span>
                                                )}
                                            </td>
                                            {/* Start Date */}
                                            <td>
                                                <span className="sr-mono">{fmtPayDate(p.StartDate)}</span>
                                            </td>
                                            {/* End Date */}
                                            <td>
                                                <span className="sr-mono">{fmtPayDate(p.EndDate)}</span>
                                            </td>
                                            {/* Check Date */}
                                            <td>
                                                {p.CheckDate
                                                    ? <span className="sr-mono">{fmtPayDate(p.CheckDate)}</span>
                                                    : <span className="dash">—</span>
                                                }
                                            </td>
                                            {/* Year */}
                                            <td>
                                                <span className="sr-mono">{p.AccountingYear}</span>
                                            </td>
                                            {/* ID */}
                                            <td>
                                                <span className="sr-mono">{p.AltPayPeriodID || "—"}</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                {/* No pager — a full year renders in one scroll. */}

            </div>
        </PageWrapper>
    );
};

export default PayPeriodsPage;
